import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import axiosInstance from "Axiosinstance";

/**
 * Modal for editing a single branch.
 *
 * Props:
 * - isOpen: boolean
 * - toggle: () => void
 * - companyUid: string - parent company's uid
 * - branch: object - the branch being edited ({_id, branch_name, branch_address, branch_state, branch_gst_no})
 * - onSaved: () => void - called after a successful save (e.g. refetch company)
 */
const BranchEditModal = ({ isOpen, toggle, companyUid, branch, onSaved }) => {
  const [form, setForm] = useState({
    branch_name: "",
    branch_address: "",
    branch_state: "",
    branch_gst_no: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (branch) {
      setForm({
        branch_name: branch.branch_name || "",
        branch_address: branch.branch_address || "",
        branch_state: branch.branch_state || "",
        branch_gst_no: branch.branch_gst_no || "",
      });
    }
  }, [branch, isOpen]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branch?._id) {
      toast.error("Missing branch id");
      return;
    }

    // Ensure no field is just whitespace
    const trimmed = {
      branch_name: form.branch_name.trim(),
      branch_address: form.branch_address.trim(),
      branch_state: form.branch_state.trim(),
      branch_gst_no: form.branch_gst_no.trim(),
    };
    if (Object.values(trimmed).some((v) => !v)) {
      toast.error("All fields are required");
      return;
    }

    const branchLimits = {
      branch_name: 30,
      branch_address: 200,
      branch_state: 20,
      branch_gst_no: 25,
    };
    const invalidField = Object.entries(branchLimits).find(
      ([field, limit]) => trimmed[field].length > limit,
    );
    if (invalidField) {
      const [field, limit] = invalidField;
      const labels = {
        branch_name: "Branch name",
        branch_address: "Branch address",
        branch_state: "Branch state",
        branch_gst_no: "Branch GST number",
      };
      toast.error(`${labels[field]} must be ${limit} characters or fewer`);
      return;
    }

    setSaving(true);
    try {
      const { data } = await axiosInstance.post("/api/partner/branchedit", {
        company_uid: companyUid,
        branch_uid: branch.branch_uid,
        ...trimmed,
      });

      if (data?.status) {
        toast.success(data?.message || "Branch updated successfully");
        onSaved && onSaved();
        toggle();
      } else {
        toast.error(data?.message || "Unable to update branch");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Unable to update branch");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>Edit Branch</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="branch_name">Branch Name</Label>
            <Input
              id="branch_name"
              type="text"
              maxLength="50"
              value={form.branch_name}
              onChange={handleChange("branch_name")}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label for="branch_address">Branch Address</Label>
            <Input
              id="branch_address"
              type="textarea"
              maxLength="500"
              value={form.branch_address}
              onChange={handleChange("branch_address")}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label for="branch_state">State</Label>
            <Input
              id="branch_state"
              type="text"
              maxLength="50"
              value={form.branch_state}
              onChange={handleChange("branch_state")}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label for="branch_gst_no">GST Number</Label>
            <Input
              id="branch_gst_no"
              type="text"
              maxLength="50"
              value={form.branch_gst_no}
              onChange={handleChange("branch_gst_no")}
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={toggle} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Save Changes"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default BranchEditModal;
